import React from 'react';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonButtons,
  IonBackButton,
  useIonViewWillLeave,
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  warningOutline,
  closeCircleOutline,
} from 'ionicons/icons';
import SnowballMenu from '@/modules/user/SnowballMenu';
import PageRefresher from '@/components/PageRefresher';
import { useContactPicker } from '@/contexts/ContactPickerContext';
import { Capacitor } from '@capacitor/core';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';

const Help: React.FC = () => {
  const {
    deviceDetails: deviceInfo,
    isIOS,
    isLoading: deviceInfoLoading,
  } = useDeviceInfo();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const {
    isAvailable: isContactPickerAvailable,
    canBeEnabled: pickerContactCanBeEnabled,
  } = useContactPicker();

  const getContactPickerStatus = () => {
    if (deviceInfoLoading) {
      return {
        icon: checkmarkCircleOutline,
        color: 'success',
        text: 'Available',
      };
    }

    // eslint-disable-next-line no-console
    console.log('Contact Picker Status Check:', {
      isContactPickerSupportEnabled: isContactPickerAvailable,
      isIOS,
      isSecureContext,
      platform: Capacitor.getPlatform(),
    });

    if (isContactPickerAvailable) {
      return {
        icon: checkmarkCircleOutline,
        color: 'success',
        text: 'Available',
      };
    }
    if (pickerContactCanBeEnabled) {
      return {
        icon: warningOutline,
        color: 'warning',
        text: 'Available on the device but not enabled',
      };
    }
    return {
      icon: closeCircleOutline,
      color: 'danger',
      text: 'Not supported on this device',
    };
  };

  const contactPickerStatus = getContactPickerStatus();

  const hasNavigatorContacts = 'contacts' in navigator;
  const isSecureCtx = window.isSecureContext;
  const isChrome =
    /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const diagnosticItems = [
    { label: 'Protocol', value: window.location.protocol },
    { label: 'Hostname', value: window.location.hostname },
    {
      label: 'Secure Context',
      value: isSecureCtx ? 'Yes' : 'No',
      ok: isSecureCtx,
    },
    {
      label: 'Navigator.contacts',
      value: hasNavigatorContacts ? 'Yes' : 'No',
      ok: hasNavigatorContacts,
    },
    { label: 'Chrome Browser', value: isChrome ? 'Yes' : 'No' },
    { label: 'Mobile Device', value: isMobile ? 'Yes' : 'No' },
  ];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/topics" />
          </IonButtons>
          <IonTitle>Help</IonTitle>
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <PageRefresher />
        <div style={{ padding: '20px' }}>
          <IonCard>
            <IonCardContent>
              <IonList inset={false}>
                <IonItem lines="full">
                  <IonLabel>
                    <p>Contact Picker Status</p>
                    <h2>
                      <IonIcon
                        icon={contactPickerStatus.icon}
                        color={contactPickerStatus.color}
                        style={{ marginRight: '6px', verticalAlign: 'middle' }}
                      />
                      {contactPickerStatus.text}
                    </h2>
                  </IonLabel>
                </IonItem>
                {diagnosticItems.map(({ label, value, ok }) => (
                  <IonItem key={label} lines="full">
                    <IonLabel>
                      <p>{label}</p>
                      <h2>
                        {ok !== undefined && (
                          <IonIcon
                            icon={
                              ok ? checkmarkCircleOutline : closeCircleOutline
                            }
                            color={ok ? 'success' : 'danger'}
                            style={{
                              marginRight: '6px',
                              verticalAlign: 'middle',
                            }}
                          />
                        )}
                        {value}
                      </h2>
                    </IonLabel>
                  </IonItem>
                ))}
                {deviceInfo && (
                  <>
                    <IonItem lines="full">
                      <IonLabel>
                        <p>Device Info</p>
                        <h2>
                          {deviceInfo.platform} | {deviceInfo.operatingSystem}{' '}
                          {deviceInfo.osVersion} | {deviceInfo.manufacturer}{' '}
                          {deviceInfo.model}
                          {deviceInfo.webViewVersion &&
                            ` | WebView ${deviceInfo.webViewVersion}`}
                        </h2>
                      </IonLabel>
                    </IonItem>
                    <IonItem lines="none">
                      <IonLabel>
                        <p>iOS Device</p>
                        <h2>{isIOS ? 'Yes' : 'No'}</h2>
                      </IonLabel>
                    </IonItem>
                  </>
                )}
              </IonList>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Help;
